import { ReactNode, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next';

/**
 * Reusable two-column sidebar layout.
 *
 * Desktop (md+): sidebar is rendered inline to the left, sticky at the top so
 * it scrolls with the viewport. Content fills the remaining width.
 *
 * Mobile (<md): sidebar collapses into a temporary Drawer, triggered by a
 * hamburger button that sits above the content. Clicks inside the drawer
 * auto-close it (same pattern as the global Header drawer).
 */

export interface SectionLayoutProps {
  sidebar: ReactNode;
  children: ReactNode;
  sidebarWidth?: number;
  mobileNavLabel?: string;
}

export function SectionLayout({
  sidebar,
  children,
  sidebarWidth = 240,
  mobileNavLabel,
}: SectionLayoutProps) {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<MenuIcon />}
          onClick={() => setMobileOpen(true)}
          fullWidth
        >
          {mobileNavLabel ?? t('common.menu')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        <Box
          component="aside"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: sidebarWidth,
            flexShrink: 0,
            position: 'sticky',
            top: 16,
          }}
        >
          {sidebar}
        </Box>

        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
          }}
        >
          <Box onClick={() => setMobileOpen(false)} sx={{ height: '100%' }}>
            {sidebar}
          </Box>
        </Drawer>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
      </Box>
    </>
  );
}
